// ============================================================================
// COMPONENTE: CaseProgressBar.jsx — Indicador de Progresso do Caso
// Exibe os estágios do caso como barra horizontal tipo stepper.
// Oculta-se automaticamente se stage não estiver definido.
// ============================================================================

import React from 'react';
import { Check, Info } from 'lucide-react';

const STAGES = [
  {
    key: 'pre_processual',
    label: 'Pré-Processual',
    info: 'Ainda não abrimos o processo no tribunal. Estamos reunindo os documentos e preparando a petição inicial.',
  },
  {
    key: 'peticao_inicial',
    label: 'Petição Inicial',
    info: 'O processo foi aberto no tribunal. A petição inicial é o documento que explica nosso pedido ao juiz.',
  },
  {
    key: 'em_andamento',
    label: 'Em Andamento',
    info: 'O processo está tramitando. As duas partes apresentam argumentos e provas ao juiz.',
  },
  {
    key: 'recurso',
    label: 'Recurso',
    info: 'A decisão foi proferida e estamos contestando em uma instância superior, pedindo revisão.',
  },
  {
    key: 'encerrado',
    label: 'Encerrado',
    info: 'O processo foi concluído. A decisão é definitiva e não pode mais ser contestada.',
  },
];

function StageTooltip({ text }) {
  return (
    <span className="group relative inline-flex items-center cursor-help mt-0.5">
      <Info className="w-2.5 h-2.5 text-gray-400 group-hover:text-[#C5A059] transition-colors" />
      <span className="pointer-events-none absolute bottom-full left-0 mb-2 w-48 rounded-lg bg-[#20243B] px-2.5 py-2 text-white text-xs shadow-xl opacity-0 transition-opacity duration-200 group-hover:opacity-100 z-50 leading-relaxed font-normal tracking-normal normal-case">
        {text}
        <span className="absolute top-full left-2 border-4 border-transparent border-t-[#20243B]" />
      </span>
    </span>
  );
}

export default function CaseProgressBar({ stage }) {
  const idx = STAGES.findIndex((s) => s.key === stage);
  if (idx === -1) return null;

  return (
    <div className="border border-gray-300 rounded-lg bg-white px-4 py-3">
      <div className="text-[10px] font-bold uppercase tracking-wide text-gray-500 mb-2.5">
        Progresso do caso
      </div>

      <div className="flex items-center gap-0">
        {STAGES.map((s, i) => {
          const isPast    = i < idx;
          const isCurrent = i === idx;
          const isFuture  = i > idx;
          const isLast    = i === STAGES.length - 1;

          return (
            <React.Fragment key={s.key}>
              {/* Nó */}
              <div className="flex flex-col items-center shrink-0" style={{ minWidth: 0 }}>
                {/* Círculo */}
                <div
                  className={`flex items-center justify-center rounded-full transition-all ${
                    isPast
                      ? 'w-5 h-5 bg-[#20243B]'
                      : isCurrent
                      ? 'w-6 h-6 bg-[#C5A059] ring-2 ring-[#C5A059]/30'
                      : 'w-5 h-5 bg-white border-2 border-gray-300'
                  }`}
                >
                  {isPast && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                  {isCurrent && <span className="w-2 h-2 rounded-full bg-white block" />}
                </div>

                {/* Label + Tooltip */}
                <div className="flex flex-col items-center mt-1.5" style={{ maxWidth: '70px' }}>
                  <span
                    className={`text-center leading-tight ${
                      isCurrent
                        ? 'text-[10px] font-bold text-[#C5A059]'
                        : isPast
                        ? 'text-[9px] font-semibold text-[#20243B]'
                        : 'text-[9px] text-gray-400'
                    }`}
                    style={{ wordBreak: 'break-word' }}
                  >
                    {s.label}
                  </span>
                  <StageTooltip text={s.info} />
                </div>
              </div>

              {/* Linha conectora */}
              {!isLast && (
                <div
                  className={`flex-1 h-0.5 mb-4 mx-0.5 ${
                    i < idx ? 'bg-[#20243B]' : 'bg-gray-200'
                  }`}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
